import { FC, useMemo } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import type { TemplateFormSchema } from '../../../../validations/edit-template.validation';

import { InfoCircle } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import { Cursor, Tooltip } from '../../../../../../../components/tooltip/index';
import { MappingEditorStore, VariableMappingType } from '../../../../store/mapping-editor.store';
import { inferMappingTypeFromStoredValueAndOptions } from '../../../../utils/infer-variable-mapping-type-from-options.util';
import { resolveFieldOptions } from '../../../../utils/resolve-field-options.util';
import { VariableMappingField } from './variable-mapping-field';

type VariableMappingsSidebarProps = {
  control: Control<TemplateFormSchema>;
  setValue: UseFormSetValue<TemplateFormSchema>;
};

export const VariableMappingsSidebar: FC<VariableMappingsSidebarProps> = ({
  control,
  setValue,
}) => {
  const variableMappingTypes = MappingEditorStore(
    ({ variableMappingTypes }) => variableMappingTypes,
  );
  const templateContext = MappingEditorStore(({ templateContext }) => templateContext);
  const setVariableMapping = MappingEditorStore(({ setVariableMapping }) => setVariableMapping);
  const detectedVariables = (useWatch({
    control,
    name: 'stepThree.detectedVariables',
    defaultValue: [],
  }) ?? []) as string[];
  const fieldMapping = (useWatch({ control, name: 'stepThree.fieldMapping', defaultValue: {} }) ??
    {}) as Record<string, string>;
  const fieldMappingTypesForm =
    useWatch({
      control,
      name: 'stepThree.fieldMappingTypes',
      defaultValue: {},
    }) ?? {};

  const fieldOptions = useMemo(() => resolveFieldOptions(templateContext), [templateContext]);

  const mappedCount = detectedVariables.filter(
    (v) => (fieldMapping[v]?.trim?.() ?? '') !== '',
  ).length;

  if (detectedVariables.length === 0) return null;

  return (
    <aside
      className="flex w-[280px] shrink-0 flex-col rounded-2xl bg-white"
      style={{
        boxShadow:
          '0 -1px 3px 0 rgba(16, 24, 40, 0.04), 0 2px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06)',
      }}
    >
      <div className="flex items-center justify-between px-4 pb-4 pt-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="VARIABLE_MAPPINGS" />
          </span>
          <Tooltip
            className="mr-2 w-[480px]"
            content={<AppFormattedMessage id="VARIABLE_MAPPINGS_TOOLTIP" />}
            cursor={Cursor.POINTER}
            placement="top"
          >
            <button className="text-xs text-brand-700 underline" type="button">
              <InfoCircle className="size-4" iconColor="#667085" />
            </button>
          </Tooltip>
        </div>
        <span
          className={twMerge(
            'text-xs font-medium transition-colors duration-200',
            mappedCount == detectedVariables.length ? 'text-forest-600' : 'text-sunbeam-500',
          )}
        >
          {mappedCount}/{detectedVariables.length}
        </span>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-4">
        {detectedVariables.map((varName) => (
          <div key={varName}>
            <VariableMappingField
              mappingType={
                inferMappingTypeFromStoredValueAndOptions(
                  fieldMapping[varName] ?? '',
                  fieldOptions,
                ) ??
                fieldMappingTypesForm[varName] ??
                variableMappingTypes[varName] ??
                VariableMappingType.SYSTEM
              }
              onMappingChange={(value, type) => {
                setVariableMapping(varName, value, type);
                setValue(
                  'stepThree.fieldMapping',
                  { ...fieldMapping, [varName]: value },
                  { shouldValidate: true },
                );
                setValue(
                  'stepThree.fieldMappingTypes',
                  { ...fieldMappingTypesForm, [varName]: type },
                  { shouldValidate: true },
                );
              }}
              value={fieldMapping[varName] ?? ''}
              varName={varName}
            />
          </div>
        ))}
      </div>
    </aside>
  );
};
